import React from 'react';
import { Avatar, makeStyles, shorthands, tokens, useId } from '@fluentui/react-components';
import { StatUser } from '../../utils/statApi/UsersApi';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingVerticalL,
    flexWrap: 'wrap',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXXL),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  details: {
    flexGrow: 1,
    flexBasis: '240px',
    minWidth: 0,
  },
  title: {
    marginTop: 0,
    marginBottom: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingVerticalL,
    ...shorthands.margin(0),
    '@media (max-width: 1023px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 639px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  graphField: {
    '@media (min-width: 640px) and (max-width: 1023px)': {
      gridColumn: '1 / -1',
    },
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    marginBottom: tokens.spacingVerticalXS,
  },
  value: {
    ...shorthands.margin(0),
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    overflowWrap: 'anywhere',
  },
});

interface UserIdentityCardProps {
  user: StatUser;
  imageUrl?: string | null;
}

const UserIdentityCard = ({ user, imageUrl }: UserIdentityCardProps): JSX.Element => {
  const styles = useStyles();
  const titleId = useId('user-identity-title');
  const graphId = user.graphGUID.replace(
    /^([0-9a-f]{8})([0-9a-f]{4})([0-9a-f]{4})([0-9a-f]{4})([0-9a-f]{12})$/i,
    '$1-$2-$3-$4-$5',
  );

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <Avatar
        name={user.displayName}
        image={imageUrl ? { src: imageUrl } : undefined}
        aria-label={`${user.displayName} profile picture`}
        color="colorful"
        size={72}
      />
      <div className={styles.details}>
        <h2 id={titleId} className={styles.title}>
          Your identity
        </h2>
        <dl className={styles.fields}>
          <div>
            <dt className={styles.label}>Name</dt>
            <dd className={styles.value}>{user.displayName}</dd>
          </div>
          <div>
            <dt className={styles.label}>Alias</dt>
            <dd className={styles.value}>{user.alias}</dd>
          </div>
          <div className={styles.graphField}>
            <dt className={styles.label}>Graph ID</dt>
            <dd className={styles.value}>{graphId}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
};

export default UserIdentityCard;
